import assert from 'assert';

import {
  FieldDefinitionType,
  LinkDefinitionOrInaccessibleInfoNode,
  TagSchemaItemType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

export interface FieldDefinitionForValidation {
  __typename: string;
  type: FieldDefinitionType;
  isMulti?: boolean;
}

// Centralized mapping between schema `FieldType` and GraphQL `FieldDefinitionType`.
export const FIELD_TYPE_TO_FIELD_DEFINITION_TYPE: Readonly<Record<FieldType, FieldDefinitionType>> = {
  [FieldType.TEXT]: FieldDefinitionType.TEXT,
  [FieldType.LONG_TEXT]: FieldDefinitionType.LONG_TEXT,
  [FieldType.INTEGER]: FieldDefinitionType.INTEGER,
  [FieldType.FLOAT]: FieldDefinitionType.FLOAT,
  [FieldType.BOOLEAN]: FieldDefinitionType.BOOLEAN,
  [FieldType.DATE]: FieldDefinitionType.DATE,
  [FieldType.DATETIME]: FieldDefinitionType.DATETIME,
  [FieldType.JSON]: FieldDefinitionType.JSON,
  [FieldType.SELECTOR]: FieldDefinitionType.DROPDOWN,
  [FieldType.AA_PART_LINK]: FieldDefinitionType.AA_PART_LINK,
  [FieldType.ASSAY_REQUEST_LINK]: FieldDefinitionType.ASSAY_REQUEST_LINK,
  [FieldType.ASSAY_RESULT_LINK]: FieldDefinitionType.ASSAY_RESULT_LINK,
  [FieldType.ASSAY_RUN_LINK]: FieldDefinitionType.ASSAY_RUN_LINK,
  [FieldType.BATCH_LINK]: FieldDefinitionType.BATCH_LINK,
  [FieldType.BLOB_LINK]: FieldDefinitionType.BLOB_LINK,
  [FieldType.ENTRY_LINK]: FieldDefinitionType.ENTRY_LINK,
  [FieldType.BIOENTITY_LINK]: FieldDefinitionType.ENTITY_LINK,
  [FieldType.PART_LINK]: FieldDefinitionType.PART_LINK,
  [FieldType.SAMPLE_LINK]: FieldDefinitionType.SAMPLE_LINK,
  [FieldType.STORABLE_LINK]: FieldDefinitionType.STORAGE_LINK,
  [FieldType.TRANSLATION_LINK]: FieldDefinitionType.TRANSLATION_LINK,
  [FieldType.TRANSCRIPTION_LINK]: FieldDefinitionType.TRANSCRIPTION_LINK,
  [FieldType.BASE_MOLECULE_LINK]: FieldDefinitionType.BASE_MOLECULE_LINK,
};

export function fieldTypeToFieldDefinitionType(fieldType: FieldType): FieldDefinitionType {
  const fieldDefinitionType = FIELD_TYPE_TO_FIELD_DEFINITION_TYPE[fieldType];
  assert(fieldDefinitionType != null, `Unsupported field type: ${fieldType}`);
  return fieldDefinitionType;
}

interface TagSchemaNode {
  __typename: 'TagSchemaNode';
  id: string;
  itemType: TagSchemaItemType;
  folderItemType: string;
}

/** Interface reference for schema link fields that link to a schema interface. */
interface SchemaInterfaceNode {
  __typename: 'SchemaInterfaceNode';
  id: string;
  /** IDs of schemas that implement this interface */
  schemas?: ReadonlyArray<{id: string}>;
}

interface HasSchema {
  schema:
    | TagSchemaNode
    | {__typename: 'InaccessibleInfoNode'}
    | {__typename: Exclude<string, 'TagSchemaNode' | 'InaccessibleInfoNode'>; id: string}
    | null;
  /** Set when the field links to a custom or static schema interface (fieldset/system category). */
  interface?: SchemaInterfaceNode | {__typename: 'InaccessibleInfoNode'} | null;
}

interface HasLinkDefinition {
  linkDefinition: LinkDefinitionOrInaccessibleInfoNode | null;
}

export type DropdownLinkFieldDefinitionForValidation = FieldDefinitionForValidation & HasLinkDefinition;
export type SchemaLinkFieldDefinitionForValidation = FieldDefinitionForValidation & HasSchema;
export type LinkDefinitionFieldDefinitionForValidation = FieldDefinitionForValidation & HasLinkDefinition;

function tagSchemaItemTypeToFieldDefinitionType(itemType: TagSchemaItemType): FieldDefinitionType {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return FieldDefinitionType.AA_SEQUENCE_LINK;
    case TagSchemaItemType.DNA_SEQUENCE:
    case TagSchemaItemType.DNA_OLIGO:
    case TagSchemaItemType.RNA_OLIGO:
    case TagSchemaItemType.RNA_SEQUENCE:
    case TagSchemaItemType.MIXED:
      return FieldDefinitionType.DNA_SEQUENCE_LINK;
    case TagSchemaItemType.ENTRY:
      return FieldDefinitionType.ENTRY_LINK;
    case TagSchemaItemType.MIXTURE:
      return FieldDefinitionType.MIXTURE_LINK;
    case TagSchemaItemType.MOLECULE:
      return FieldDefinitionType.MOLECULE_LINK;
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
    case TagSchemaItemType.ANTIBODY:
    case TagSchemaItemType.EQUIPMENT:
    case TagSchemaItemType.OLIGO_CONJUGATE:
    case TagSchemaItemType.OLIGO_DUPLEX:
    case TagSchemaItemType.TRIAL_SUBJECT:
      return FieldDefinitionType.CUSTOM_ENTITY_LINK;
    default:
      assertNever(itemType);
      assert(false, `Unknown tag schema item type: ${itemType}`);
  }
}

// Exhaustive map: TypeScript will error if any FieldDefinitionType is missing.
export const FIELD_TYPE_SUBTYPES: Readonly<Record<FieldDefinitionType, ReadonlyArray<FieldDefinitionType>>> =
  {
    // Types with subtypes
    [FieldDefinitionType.ENTITY_LINK]: [
      FieldDefinitionType.AA_PART_LINK,
      FieldDefinitionType.AA_SEQUENCE_LINK,
      FieldDefinitionType.CUSTOM_ENTITY_LINK,
      FieldDefinitionType.DNA_SEQUENCE_LINK,
      FieldDefinitionType.MIXTURE_LINK,
      FieldDefinitionType.MOLECULE_LINK,
      FieldDefinitionType.PART_LINK,
      FieldDefinitionType.TRANSCRIPTION_LINK,
      FieldDefinitionType.TRANSLATION_LINK,
    ],
    [FieldDefinitionType.LONG_TEXT]: [FieldDefinitionType.TEXT],
    [FieldDefinitionType.TEXT]: [FieldDefinitionType.LONG_TEXT],
    // Types without subtypes
    [FieldDefinitionType.AA_PART_LINK]: [],
    [FieldDefinitionType.AA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.ASSAY_REQUEST_LINK]: [],
    [FieldDefinitionType.ASSAY_RESULT_LINK]: [],
    [FieldDefinitionType.ASSAY_RUN_LINK]: [],
    [FieldDefinitionType.BATCH_LINK]: [],
    [FieldDefinitionType.BLOB_LINK]: [],
    [FieldDefinitionType.BOOLEAN]: [],
    [FieldDefinitionType.CUSTOM_ENTITY_LINK]: [],
    [FieldDefinitionType.DATE]: [],
    [FieldDefinitionType.DATETIME]: [],
    [FieldDefinitionType.DNA_SEQUENCE_LINK]: [],
    [FieldDefinitionType.DROPDOWN]: [],
    [FieldDefinitionType.ENTRY_LINK]: [],
    [FieldDefinitionType.FLOAT]: [],
    [FieldDefinitionType.INTEGER]: [],
    [FieldDefinitionType.JSON]: [],
    [FieldDefinitionType.MIXTURE_LINK]: [],
    [FieldDefinitionType.MOLECULE_LINK]: [],
    [FieldDefinitionType.PART_LINK]: [],
    [FieldDefinitionType.SAMPLE_LINK]: [],
    [FieldDefinitionType.STORAGE_LINK]: [],
    [FieldDefinitionType.TRANSCRIPTION_LINK]: [],
    [FieldDefinitionType.TRANSLATION_LINK]: [],
    [FieldDefinitionType.BASE_MOLECULE_LINK]: [],
  };

/**
 * Maps GraphQL FieldDefinitionType to client-side FieldType.
 *
 * Note: This is a lossy (many-to-one) mapping. Multiple FieldDefinitionType values
 * map to a single FieldType (e.g., CUSTOM_ENTITY_LINK, AA_SEQUENCE_LINK, DNA_SEQUENCE_LINK,
 * ENTITY_LINK, MIXTURE_LINK, and MOLECULE_LINK all map to BIOENTITY_LINK).
 */
const FIELD_DEFINITION_TYPE_TO_FIELD_TYPE: Readonly<Record<FieldDefinitionType, FieldType>> = {
  [FieldDefinitionType.TEXT]: FieldType.TEXT,
  [FieldDefinitionType.LONG_TEXT]: FieldType.LONG_TEXT,
  [FieldDefinitionType.INTEGER]: FieldType.INTEGER,
  [FieldDefinitionType.FLOAT]: FieldType.FLOAT,
  [FieldDefinitionType.DATE]: FieldType.DATE,
  [FieldDefinitionType.DATETIME]: FieldType.DATETIME,
  [FieldDefinitionType.AA_PART_LINK]: FieldType.AA_PART_LINK,
  [FieldDefinitionType.ASSAY_REQUEST_LINK]: FieldType.ASSAY_REQUEST_LINK,
  [FieldDefinitionType.ASSAY_RESULT_LINK]: FieldType.ASSAY_RESULT_LINK,
  [FieldDefinitionType.ASSAY_RUN_LINK]: FieldType.ASSAY_RUN_LINK,
  [FieldDefinitionType.BATCH_LINK]: FieldType.BATCH_LINK,
  [FieldDefinitionType.BLOB_LINK]: FieldType.BLOB_LINK,
  [FieldDefinitionType.BOOLEAN]: FieldType.BOOLEAN,
  [FieldDefinitionType.CUSTOM_ENTITY_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.AA_SEQUENCE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.DNA_SEQUENCE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.ENTITY_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.DROPDOWN]: FieldType.SELECTOR,
  [FieldDefinitionType.ENTRY_LINK]: FieldType.ENTRY_LINK,
  [FieldDefinitionType.JSON]: FieldType.JSON,
  [FieldDefinitionType.MIXTURE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.MOLECULE_LINK]: FieldType.BIOENTITY_LINK,
  [FieldDefinitionType.PART_LINK]: FieldType.PART_LINK,
  [FieldDefinitionType.SAMPLE_LINK]: FieldType.SAMPLE_LINK,
  [FieldDefinitionType.STORAGE_LINK]: FieldType.STORABLE_LINK,
  [FieldDefinitionType.TRANSLATION_LINK]: FieldType.TRANSLATION_LINK,
  [FieldDefinitionType.TRANSCRIPTION_LINK]: FieldType.TRANSCRIPTION_LINK,
  [FieldDefinitionType.BASE_MOLECULE_LINK]: FieldType.BASE_MOLECULE_LINK,
};

const BIOENTITY_LINK_TYPENAMES = [
  'AaPartLinkFieldDefinitionNode',
  'AaSequenceLinkFieldDefinitionNode',
  'AntibodyLinkFieldDefinitionNode',
  'CustomEntityLinkFieldDefinitionNode',
  'DnaOligoLinkFieldDefinitionNode',
  'DnaPartLinkFieldDefinitionNode',
  'DnaSequenceLinkFieldDefinitionNode',
  'AnyEntityLinkFieldDefinitionNode',
  'EquipmentLinkFieldDefinitionNode',
  'MixtureLinkFieldDefinitionNode',
  'MoleculeLinkFieldDefinitionNode',
  'OligoConjugateLinkFieldDefinitionNode',
  'OligoDuplexLinkFieldDefinitionNode',
  'RnaOligoLinkFieldDefinitionNode',
  'RnaSequenceLinkFieldDefinitionNode',
  'TranscriptionLinkFieldDefinitionNode',
  'TranslationLinkFieldDefinitionNode',
];

export function fieldDefinitionTypeToFieldType(fieldDefinitionType: FieldDefinitionType): FieldType {
  return FIELD_DEFINITION_TYPE_TO_FIELD_TYPE[fieldDefinitionType];
}

/**
 * Checks if sourceType is compatible with targetType.
 * Compatible means either equal or sourceType is a subtype of targetType.
 */
function isTypeOrSubtype(sourceType: FieldDefinitionType, targetType: FieldDefinitionType): boolean {
  if (sourceType === targetType) {
    return true;
  }
  return FIELD_TYPE_SUBTYPES[targetType]?.includes(sourceType) ?? false;
}

function resolveTypeFromSchemaLink(field: SchemaLinkFieldDefinitionForValidation): FieldDefinitionType {
  // For schema link field definitions we use ENTITY_LINK if it's "Any entity" or a specific schema link. Otherwise
  // we use the category of item as the type i.e. Any DNA Sequence is DNA_SEQUENCE_LINK. Therefore, when there is a
  // schema link we check the itemType of the schema to determine compatibility.
  const schema = field.schema;
  if (schema != null && !isInaccessible(schema) && isTagSchemaNode(schema)) {
    return tagSchemaItemTypeToFieldDefinitionType(schema.itemType);
  }
  return field.type;
}

export function areTypesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  // AnyEntityLinkFieldDefinitionNode is compatible with any other bioentity link definition
  const targetAllowsAnyBioentity =
    targetField.__typename === 'AnyEntityLinkFieldDefinitionNode' ||
    targetField.__typename === 'FieldsetLinkFieldDefinitionNode' ||
    targetField.__typename === 'SystemCategoryFieldDefinitionNode';

  if (BIOENTITY_LINK_TYPENAMES.includes(sourceField.__typename) && targetAllowsAnyBioentity) {
    return true;
  }

  // Allow SchemaLinkFieldDefinitionNode (specific schema) to be compatible with
  // AnyEntityLinkFieldDefinitionNode (any entity) - a specific schema can map to "any entity"
  const isSpecificToAnyEntityMapping =
    sourceField.__typename === 'SchemaLinkFieldDefinitionNode' &&
    (targetAllowsAnyBioentity || targetField.__typename === 'BaseFieldDefinitionNode');

  // For specific schema -> any entity mapping, resolve the source type and check compatibility
  if (isSpecificToAnyEntityMapping && isSchemaLinkFieldDefinition(sourceField)) {
    const resolvedSourceType = resolveTypeFromSchemaLink(sourceField);
    return isTypeOrSubtype(resolvedSourceType, targetField.type);
  }

  if (sourceField.type === FieldDefinitionType.TEXT || sourceField.type === FieldDefinitionType.LONG_TEXT) {
    // Text and Long Text are compatible with each other
    return isTypeOrSubtype(sourceField.type, targetField.type);
  }

  if (sourceField.__typename !== targetField.__typename) {
    return false;
  }

  if (isSchemaLinkFieldDefinition(targetField) && isSchemaLinkFieldDefinition(sourceField)) {
    const resolvedSourceType = resolveTypeFromSchemaLink(sourceField);
    const resolvedTargetType = resolveTypeFromSchemaLink(targetField);
    return isTypeOrSubtype(resolvedSourceType, resolvedTargetType);
  }

  return targetField.type === sourceField.type;
}

export function areCardinalitiesCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): boolean {
  if ((sourceField.isMulti ?? false) && !(targetField.isMulti ?? false)) {
    return false;
  }
  return true;
}

function isDropdownLinkFieldDefinition(
  field: FieldDefinitionForValidation
): field is DropdownLinkFieldDefinitionForValidation {
  return field.__typename === 'DropdownLinkFieldDefinitionNode';
}

export function isAnyDropdownFieldDefinition(
  field: FieldDefinitionForValidation
): field is DropdownLinkFieldDefinitionForValidation {
  return isDropdownLinkFieldDefinition(field);
}

function isSchemaLinkFieldDefinition(
  field: FieldDefinitionForValidation
): field is SchemaLinkFieldDefinitionForValidation {
  return field.__typename === 'SchemaLinkFieldDefinitionNode';
}

function hasLinkDefinitionProperty(
  field: FieldDefinitionForValidation
): field is LinkDefinitionFieldDefinitionForValidation {
  return field.hasOwnProperty('linkDefinition');
}

function isTagSchemaNode(
  schema: NonNullable<SchemaLinkFieldDefinitionForValidation['schema']>
): schema is TagSchemaNode {
  return schema.__typename === 'TagSchemaNode';
}

function getDropdownId(field: DropdownLinkFieldDefinitionForValidation): string | null {
  if (field.linkDefinition != null && !isInaccessible(field.linkDefinition)) {
    return field.linkDefinition.id;
  }
  return null;
}

export function areDropdownsCompatible(
  sourceField: DropdownLinkFieldDefinitionForValidation,
  targetField: DropdownLinkFieldDefinitionForValidation
): boolean {
  return getDropdownId(sourceField) === getDropdownId(targetField);
}

function getSchemaId(field: SchemaLinkFieldDefinitionForValidation): string | null {
  if (field.schema == null || isInaccessible(field.schema)) {
    return null;
  }
  return field.schema.id;
}

function getInterfaceId(field: SchemaLinkFieldDefinitionForValidation): string | null {
  const schemaInterface = field.interface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return null;
  }
  return schemaInterface.id;
}

/** Returns IDs of schemas that implement the field's interface. */
function getImplementingSchemaIds(field: SchemaLinkFieldDefinitionForValidation): ReadonlyArray<string> {
  const schemaInterface = field.interface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return [];
  }
  const schemas = schemaInterface.schemas;
  if (schemas == null) {
    return [];
  }
  return schemas.map((s) => s.id);
}

/**
 * When target is a schema interface field, source must either
 * link to the same interface or have a schema that implements that interface.
 */
export function areInterfacesCompatible(
  sourceField: SchemaLinkFieldDefinitionForValidation,
  targetField: SchemaLinkFieldDefinitionForValidation
): boolean {
  const targetInterfaceId = getInterfaceId(targetField);
  if (targetInterfaceId == null) {
    return true;
  }
  const sourceInterfaceId = getInterfaceId(sourceField);
  if (sourceInterfaceId === targetInterfaceId) {
    return true;
  }
  const sourceSchemaId = getSchemaId(sourceField);
  if (sourceSchemaId != null) {
    const implementingIds = getImplementingSchemaIds(targetField);
    return implementingIds.length > 0 && implementingIds.includes(sourceSchemaId);
  }
  return false;
}

function getLinkDefinitionId(field: LinkDefinitionFieldDefinitionForValidation): string | null {
  if (field.linkDefinition == null || isInaccessible(field.linkDefinition)) {
    return null;
  }
  return field.linkDefinition.id;
}

export function areLinkSchemasCompatible(
  sourceField: SchemaLinkFieldDefinitionForValidation,
  targetField: SchemaLinkFieldDefinitionForValidation
): boolean {
  const targetSchemaId = getSchemaId(targetField);
  if (targetSchemaId == null) {
    return true;
  }
  return getSchemaId(sourceField) === targetSchemaId;
}

export function areLinkDefinitionsCompatible(
  sourceField: LinkDefinitionFieldDefinitionForValidation,
  targetField: LinkDefinitionFieldDefinitionForValidation
): boolean {
  const targetLinkDefId = getLinkDefinitionId(targetField);
  if (targetLinkDefId == null) {
    return true;
  }
  return getLinkDefinitionId(sourceField) === targetLinkDefId;
}

export interface FieldCompatibilityResult {
  isCompatible: boolean;
  incompatibilityReason: string | null;
}

export function areFieldDefinitionsCompatible(
  sourceField: FieldDefinitionForValidation,
  targetField: FieldDefinitionForValidation
): FieldCompatibilityResult {
  if (!areTypesCompatible(sourceField, targetField)) {
    return {
      isCompatible: false,
      incompatibilityReason: "Field definitions don't match: type mismatch.",
    };
  }

  if (!areCardinalitiesCompatible(sourceField, targetField)) {
    const sourceCardinality = sourceField.isMulti ? 'multiple values' : 'single value';
    const targetCardinality = targetField.isMulti ? 'multiple values' : 'single value';
    return {
      isCompatible: false,
      incompatibilityReason: `Field definitions don't match: source expects ${sourceCardinality}, target expects ${targetCardinality}.`,
    };
  }

  if (isAnyDropdownFieldDefinition(sourceField) && isAnyDropdownFieldDefinition(targetField)) {
    if (!areDropdownsCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: dropdown selector mismatch.",
      };
    }
  }

  // SchemaLinkFieldDefinition is slated to be deprecated, but for now we want to maintain compatibility checks for them
  if (isSchemaLinkFieldDefinition(sourceField) && isSchemaLinkFieldDefinition(targetField)) {
    if (!areLinkSchemasCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: schema mismatch.",
      };
    }
    if (getInterfaceId(targetField) != null && !areInterfacesCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason:
          "Field definitions don't match: source must link to the same interface or to a schema that implements the target's interface.",
      };
    }
  }

  // TODO(BNCH-143948): Add additional check for interface fields (Fieldset and SystemCategory) that verifies
  // that the specific schema satisfies the interface or that the link definitions for the interface match
  const targetIsInterface =
    targetField.__typename === 'FieldsetLinkFieldDefinitionNode' ||
    targetField.__typename === 'SystemCategoryFieldDefinitionNode';
  if (
    hasLinkDefinitionProperty(sourceField) &&
    hasLinkDefinitionProperty(targetField) &&
    !targetIsInterface
  ) {
    if (!areLinkDefinitionsCompatible(sourceField, targetField)) {
      return {
        isCompatible: false,
        incompatibilityReason: "Field definitions don't match: link definition mismatch.",
      };
    }
  }

  return {isCompatible: true, incompatibilityReason: null};
}
